import PageLayout from "@/components/PageLayout";
import PricingCalculator from "@/components/PricingCalculator";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";
import { Link } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import FAQSection from "@/components/FAQSection";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";

const Pricing = () => {
  useSEO({
    title: 'Ommni Pricing - Simple & Transparent Commerce Operations Platform Plans',
    description: 'Flexible pricing for commerce operations platform. From $500/month for growing businesses to custom enterprise solutions. All plans include inventory management, order routing, and integrations.',
    keywords: 'commerce platform pricing, PIM pricing, inventory management cost, order management pricing, enterprise retail software pricing',
    canonical: 'https://ommni.io/pricing',
  });

  const faqs = [
    {
      question: "What's included in the base pricing?",
      answer: "All plans include access to core features like Product Information Management (PIM), Inventory Management, Order Management (DOM), and basic integrations. Additional features like advanced analytics, premium support, and custom integrations are available as add-ons."
    },
    {
      question: "Are there any setup or onboarding fees?",
      answer: "Implementation services start at $5,000 and include data migration, system configuration, team training, and go-live support. The exact cost depends on your catalog size and integration requirements."
    },
    {
      question: "Can I change my plan later?",
      answer: "Yes, you can upgrade or downgrade your plan at any time. Changes take effect at the start of your next billing cycle, and we'll prorate any differences in cost."
    },
    {
      question: "Do you offer discounts for annual commitments?",
      answer: "Yes, we offer 10% discount for annual subscriptions. Enterprise customers with multi-year commitments may receive additional volume discounts. Contact our sales team for custom pricing."
    },
    {
      question: "What payment methods do you accept?",
      answer: "We accept credit cards, ACH transfers, and wire transfers. Enterprise customers can also opt for invoice-based billing with NET 30 payment terms."
    },
    {
      question: "Is there a free trial available?",
      answer: "Yes, we offer a 14-day free trial with full access to all features. No credit card required to start your trial. Our team will help you get set up and provide guided onboarding."
    },
    {
      question: "What happens if I exceed my plan limits?",
      answer: "If you exceed your plan's SKU or order limits, we'll contact you to discuss upgrading to the next tier. We provide grace periods and won't interrupt service during the transition."
    },
    {
      question: "Do you charge per user?",
      answer: "No, our pricing is based on your business volume (SKUs, orders, locations) not the number of users. You can add unlimited team members at no additional cost."
    }
  ];

  const plans = [
    {
      name: "Starter",
      price: "$500",
      description: "Perfect for growing businesses",
      features: [
        "Up to 10,000 SKUs",
        "1,000 orders per month",
        "5 inventory locations",
        "2 sales channels",
        "Email support",
        "Basic analytics",
      ],
    },
    {
      name: "Professional",
      price: "$2,500",
      description: "For established retailers",
      features: [
        "Up to 25,000 SKUs",
        "5,000 orders per month",
        "10 inventory locations",
        "5 sales channels",
        "Priority support",
        "Access to <a href='/features/ommni-flow'>OmmniFlow</a> 10 Flows",
        "Custom <a href='/integrations'>integrations</a>",
      ],
      popular: true,
    },
    {
      name: "Enterprise",
      price: "Custom",
      description: "For large-scale operations",
      features: [
        "Unlimited SKUs",
        "Unlimited orders",
        "Unlimited locations",
        "Unlimited channels",
        "24/7 dedicated support",
        "Enterprise analytics",
        "Full API access",
        "Custom development",
        "SLA guarantee",
      ],
    },
  ];

  return (
    <PageLayout>
      <div className="px-4 pb-12 md:pb-24">
        <div className="container mx-auto">
          <div className="text-center mb-8 md:mb-16">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6">
              Simple, Transparent Pricing
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto px-2">
              Choose the plan that fits your business needs. All plans include our core features.
            </p>
          </div>

          {/* Mobile Carousel */}
          <div className="block md:hidden mb-12">
            <Carousel className="w-full max-w-sm mx-auto">
              <CarouselContent>
                {plans.map((plan, index) => (
                  <CarouselItem key={index}>
                    <Card
                      className={`p-4 sm:p-6 gradient-card ${
                        plan.popular ? "border-2 border-primary shadow-glow" : ""
                      }`}
                    >
                      {plan.popular && (
                        <div className="inline-block px-3 py-1 rounded-full bg-primary text-primary-foreground text-xs sm:text-sm font-medium mb-3">
                          Most Popular
                        </div>
                      )}
                      <h3 className="text-xl sm:text-2xl font-bold mb-2">{plan.name}</h3>
                      <p className="text-sm sm:text-base text-muted-foreground mb-3">{plan.description}</p>
                      <div className="mb-4">
                        <span className="text-3xl sm:text-4xl font-bold text-gradient">{plan.price}</span>
                        {plan.price !== "Custom" && (
                          <span className="text-sm sm:text-base text-muted-foreground">/month</span>
                        )}
                      </div>
                      <ul className="space-y-2 sm:space-y-3 mb-6">
                        {plan.features.map((feature, i) => (
                          <li key={i} className="flex items-start">
                            <Check className="h-5 w-5 text-primary mr-2 flex-shrink-0 mt-0.5" />
                            <span className="text-sm">{feature}</span>
                          </li>
                        ))}
                      </ul>
                      <Button
                        variant={plan.popular ? "cta" : "outline"}
                        className="w-full"
                        size="lg"
                        asChild
                      >
                        <Link to="/contact">
                          {plan.price === "Custom" ? "Contact Sales" : "Get Started"}
                        </Link>
                      </Button>
                    </Card>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="hidden sm:flex" />
              <CarouselNext className="hidden sm:flex" />
            </Carousel>
            <div className="flex justify-center gap-2 mt-4">
              {plans.map((_, index) => (
                <div key={index} className="w-2 h-2 rounded-full bg-muted" />
              ))}
            </div>
          </div>

          {/* Desktop Grid */}
          <div className="hidden md:grid md:grid-cols-3 gap-8 mb-24">
            {plans.map((plan, index) => (
              <Card
                key={index}
                className={`p-8 gradient-card ${
                  plan.popular ? "border-2 border-primary shadow-glow" : ""
                }`}
              >
                {plan.popular && (
                  <div className="inline-block px-3 py-1 rounded-full bg-primary text-primary-foreground text-sm font-medium mb-4">
                    Most Popular
                  </div>
                )}
                <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                <p className="text-muted-foreground mb-4">{plan.description}</p>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-gradient">{plan.price}</span>
                  {plan.price !== "Custom" && (
                    <span className="text-muted-foreground">/month</span>
                  )}
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start">
                      <Check className="h-5 w-5 text-primary mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  variant={plan.popular ? "cta" : "outline"}
                  className="w-full"
                  size="lg"
                  asChild
                >
                  <Link to="/contact">
                    {plan.price === "Custom" ? "Contact Sales" : "Get Started"}
                  </Link>
                </Button>
              </Card>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8 mb-12 md:mb-24">
            <Card className="p-4 sm:p-6 md:p-8 text-center gradient-card">
              <h3 className="text-xl sm:text-2xl font-bold mb-2">Implementation Services</h3>
              <p className="text-sm sm:text-base text-muted-foreground mb-4">
                Get expert help setting up and customizing your OMMNI platform
              </p>
              <div>
                <span className="text-2xl sm:text-3xl font-bold text-gradient">Starting at $5,000</span>
              </div>
            </Card>

            <Card className="p-4 sm:p-6 md:p-8 text-center gradient-card">
              <h3 className="text-xl sm:text-2xl font-bold mb-2">Premium Support</h3>
              <p className="text-sm sm:text-base text-muted-foreground mb-4">
                Priority assistance, dedicated account management, and enhanced SLA
              </p>
              <div className="mb-4 md:mb-6">
                <span className="text-2xl sm:text-3xl font-bold text-gradient">$5,000</span>
                <span className="text-sm sm:text-base text-muted-foreground">/month</span>
              </div>
              <ul className="space-y-2 sm:space-y-3 text-left mb-4 md:mb-6">
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-primary mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">40 Prepaid Billable Support Hours</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-primary mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">Priority Queue Access</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-primary mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">Dedicated Account Manager</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-primary mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">30-minute Response SLA</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-primary mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">Phone & Video Support</span>
                </li>
              </ul>
              <Button variant="outline" className="w-full" size="default" asChild>
                <Link to="/contact">Contact Sales</Link>
              </Button>
            </Card>
          </div>

          {/* FAQ Section */}
          <FAQSection 
            faqs={faqs}
            title="Pricing FAQs"
            description="Common questions about Ommni pricing and billing."
          />

          {/* <PricingCalculator /> */}
        </div>
      </div>
    </PageLayout>
  );
};

export default Pricing;
